import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Smartphone, Server, MapPin, Zap, Mail } from 'lucide-react'

interface PatternAlert {
  id: string
  type: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  count: number
  status: 'increasing' | 'decreasing' | 'stable'
  description: string
}

interface FraudPatternsOverviewProps {
  patterns: PatternAlert[]
}

export const FraudPatternsOverview: React.FC<FraudPatternsOverviewProps> = ({ patterns }) => {
  const defaultPatterns = [
    { 
      type: 'Sequential Phones', 
      count: 12, 
      status: 'increasing', 
      severity: 'critical', 
      icon: Smartphone,
      description: 'Phone numbers in sequence (2262433171-3177)',
      details: '$210 saved from 7 blocked leads'
    },
    { 
      type: 'Data Center IPs', 
      count: 34, 
      status: 'stable', 
      severity: 'high', 
      icon: Server,
      description: 'Traffic from hosting providers (169.197.85.x)',
      details: '$7,020 saved from 234 blocked leads'
    },
    { 
      type: 'Zip Concentration', 
      count: 43, 
      status: 'critical', 
      severity: 'critical', 
      icon: MapPin,
      description: 'High volume from 80112 zip code',
      details: '$12,960 saved from 432 blocked leads'
    },
    { 
      type: 'Velocity Attacks', 
      count: 7, 
      status: 'decreasing', 
      severity: 'medium', 
      icon: Zap,
      description: '3+ submissions within 60 seconds',
      details: 'Real-time blocking active'
    },
    { 
      type: 'Domain Abuse', 
      count: 23, 
      status: 'stable', 
      severity: 'medium', 
      icon: Mail,
      description: 'Suspicious email domains (armyspy.com)',
      details: '$7,020 saved from 234 blocked leads'
    }
  ]

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-red-500 bg-red-50 dark:bg-red-950/20'
      case 'high': return 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
      case 'medium': return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20'
      default: return 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'increasing': return 'text-red-600 dark:text-red-400'
      case 'decreasing': return 'text-green-600 dark:text-green-400'
      default: return 'text-blue-600 dark:text-blue-400'
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-foreground mb-2">Pattern Detection Overview</h2>
        <p className="text-sm text-muted-foreground">
          Real-time monitoring of fraud patterns with financial impact tracking
        </p>
      </div>
      
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {defaultPatterns.map((pattern, index) => {
            const IconComponent = pattern.icon
            return (
              <Card key={index} className={`min-w-80 border-l-4 ${getSeverityColor(pattern.severity)}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                      <CardTitle className="text-base">{pattern.type}</CardTitle>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-foreground">{pattern.count}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {pattern.severity}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {pattern.description}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {pattern.details}
                    </p>
                    <div className="flex items-center justify-between pt-2">
                      <span className={`text-sm font-medium capitalize ${getStatusColor(pattern.status)}`}>
                        Status: {pattern.status}
                      </span>
                      <div className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${
                          pattern.status === 'increasing' ? 'bg-red-500 animate-pulse' :
                          pattern.status === 'decreasing' ? 'bg-green-500' : 'bg-blue-500'
                        }`}></div>
                        <span className="text-xs text-muted-foreground">Live</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}